import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { WarehousingBulkPreview, WarehousingBulkResult, WarehousingDispatch, WarehousingTransfer } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';

type OutboundAction = 'PICK' | 'STAGE' | 'LOAD' | 'TRANSFER_DISPATCH' | 'DISPATCH_EXIT';
type WorkUnit = { code: string; quantity: number; state: string };

@Component({
  selector: 'app-warehousing-desktop-outbound',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, TagModule, PageFormSectionComponent],
  template: `
    <app-page-form-section icon="pi pi-check-square" title="Desktop outbound work"
      subtitle="Confirm each physical step without scanning individual barcodes.">
      <div class="steps" aria-label="Outbound work steps">
        @for (step of steps; track step) {
          <span class="step" [class.done]="completed(step)" [class.current]="actionLabel(nextAction()) === step">
            {{ step }} <small>{{ stepCount(step) }}/{{ units().length }}</small>
          </span>
        }
      </div>
      <p class="work-note">Pick, Stage, and Load record handling progress. Stock stays on hand and reserved until the final dispatch or exit confirmation.</p>
      <div class="work-summary">
        <span><strong>{{ units().length }}</strong> reserved stock units</span>
        <span><strong>{{ totalQuantity() | number:'1.0-4' }}</strong> base quantity</span>
        <span>Current state: <strong>{{ status() }}</strong></span>
      </div>
      @if (blocker()) { <p class="work-blocker" role="alert">{{ blocker() }}</p> }
      @if (error) { <p class="work-blocker" role="alert">{{ error }}</p> }
      @if (success) { <p class="work-success" role="status">{{ success }}</p> }
      @if (nextAction() && !blocker()) {
        <p-button [label]="'Review ' + actionLabel(nextAction())" icon="pi pi-eye"
          [loading]="busy" [disabled]="busy" (click)="review()"></p-button>
      }
    </app-page-form-section>

    <p-dialog [header]="'Review ' + actionLabel(action)" [(visible)]="dialogVisible" [modal]="true"
      [draggable]="false" [resizable]="false" [style]="{ width: 'min(44rem, 95vw)' }">
      <p>{{ finalAction() ? 'Confirming this step posts stock out of the source warehouse.' :
        'Confirm that this physical step is complete. Stock remains on hand.' }}</p>
      <p><strong>{{ units().length }} stock units · {{ totalQuantity() | number:'1.0-4' }} base quantity</strong></p>
      <div class="unit-list">
        <table>
          <thead><tr><th>Stock unit</th><th>Current state</th><th>Quantity</th></tr></thead>
          <tbody>
            @for (unit of units(); track $index) {
              <tr><td>{{ unit.code }}</td><td>{{ unit.state }}</td><td>{{ unit.quantity | number:'1.0-4' }}</td></tr>
            }
          </tbody>
        </table>
      </div>
      @if (preview?.rows?.[0]?.blockedReason) {
        <p class="work-blocker" role="alert">{{ preview?.rows?.[0]?.blockedReason }}</p>
      }
      @if (error) { <p class="work-blocker" role="alert">{{ error }}</p> }
      <label for="desktop-work-reason">Reason for confirmation without scanning *</label>
      <textarea id="desktop-work-reason" rows="3" [(ngModel)]="reason"
        placeholder="Explain why barcode scanning was not used"></textarea>
      <ng-template pTemplate="footer">
        <p-button label="Cancel" severity="secondary" [outlined]="true" [disabled]="busy"
          (click)="dialogVisible = false"></p-button>
        <p-button [label]="'Confirm ' + actionLabel(action)" [loading]="busy"
          [disabled]="busy || !reason.trim() || !!preview?.blockedCount" (click)="confirm()"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .steps { display: flex; flex-wrap: wrap; gap: .5rem; margin-bottom: .7rem; }
    .step { border: 1px solid var(--surface-border); border-radius: 8px; padding: .45rem .7rem; }
    .step.done { background: #e9fdf5; color: #047857; }
    .step.current { border-color: #008575; font-weight: 700; }
    .step small { color: var(--text-color-secondary); margin-left: .3rem; }
    .work-note { color: var(--text-color-secondary); margin: .4rem 0 .8rem; }
    .work-summary { display: flex; flex-wrap: wrap; gap: .5rem 1.25rem; margin-bottom: 1rem; }
    .work-blocker { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; color: #9a3412; padding: .7rem; }
    .work-success { color: #047857; }
    .unit-list { max-height: 18rem; overflow: auto; margin-bottom: 1rem; }
    table { border-collapse: collapse; min-width: 100%; }
    th, td { border-bottom: 1px solid var(--surface-border); padding: .5rem; text-align: left; }
    label { display: block; font-weight: 700; margin-bottom: .4rem; }
    textarea { border: 1px solid var(--surface-border); border-radius: 8px; font: inherit; padding: .6rem; width: 100%; }
  `]
})
export class WarehousingDesktopOutboundComponent {
  @Input() dispatch?: WarehousingDispatch;
  @Input() transfer?: WarehousingTransfer;
  @Input() disabledReason = '';
  @Output() changed = new EventEmitter<void>();

  private service = inject(WarehousingService);
  private permissions = inject(PermissionService);
  private route = inject(ActivatedRoute);
  readonly steps = ['Pick', 'Stage', 'Load', 'Dispatch'];
  busy = false;
  dialogVisible = false;
  action?: OutboundAction;
  preview?: WarehousingBulkPreview;
  reason = '';
  error = '';
  success = '';

  private isTransfer(): boolean { return !!this.transfer; }
  private documentId(): number | undefined { return this.transfer?.id || this.dispatch?.id; }
  private shipmentNumber(): string | undefined {
    return this.transfer?.shipmentNumber || this.dispatch?.shipmentNumber
      || this.route.snapshot.queryParamMap.get('shipmentNumber') || undefined;
  }
  private deliveryNumber(): string | undefined {
    return this.transfer?.deliveryNumber || this.dispatch?.deliveryNumber
      || this.route.snapshot.queryParamMap.get('deliveryNumber') || undefined;
  }
  private linked(): boolean { return !!(this.transfer?.shipmentId || this.dispatch?.shipmentId
    || this.transfer?.deliveryId || this.dispatch?.deliveryId || this.shipmentNumber() || this.deliveryNumber()); }

  status(): string { return (this.transfer?.status || this.dispatch?.lifecycleStatus || this.dispatch?.status || '-').toUpperCase(); }
  units(): WorkUnit[] {
    const lines = this.transfer?.lines || this.dispatch?.lines || [];
    return lines.flatMap(line => (line.reservations || []).map(unit => ({
      code: unit.stockUnitCode || `Unit #${unit.stockUnitId}`,
      quantity: unit.quantity || 0,
      state: (unit.scanStatus || 'PENDING').toUpperCase()
    })));
  }
  totalQuantity(): number { return this.units().reduce((sum, unit) => sum + unit.quantity, 0); }
  private rank(state: string): number { return ({ PICKED: 1, STAGED: 2, LOADED: 3 } as Record<string, number>)[state] || 0; }
  stepCount(step: string): number {
    if (step === 'Dispatch') return ['DISPATCHED', 'DELIVERED', 'IN_TRANSIT', 'PARTIALLY_RECEIVED', 'RECEIVED'].includes(this.status()) ? this.units().length : 0;
    const rank = this.rank(step === 'Pick' ? 'PICKED' : step === 'Stage' ? 'STAGED' : 'LOADED');
    return this.units().filter(unit => this.rank(unit.state) >= rank).length;
  }
  completed(step: string): boolean { return !!this.units().length && this.stepCount(step) === this.units().length; }
  nextAction(): OutboundAction | undefined {
    const units = this.units();
    if (!units.length || ['DISPATCHED', 'DELIVERED', 'IN_TRANSIT', 'PARTIALLY_RECEIVED', 'RECEIVED', 'CANCELLED'].includes(this.status())) return;
    const rank = Math.min(...units.map(unit => this.rank(unit.state)));
    return rank === 0 ? 'PICK' : rank === 1 ? 'STAGE' : rank === 2 ? 'LOAD'
      : this.isTransfer() ? 'TRANSFER_DISPATCH' : 'DISPATCH_EXIT';
  }
  actionLabel(action?: OutboundAction): string {
    return ({ PICK: 'Pick', STAGE: 'Stage', LOAD: 'Load', TRANSFER_DISPATCH: 'Dispatch', DISPATCH_EXIT: 'Dispatch' } as Record<OutboundAction, string>)[action!] || '';
  }
  finalAction(): boolean { return this.action === 'TRANSFER_DISPATCH' || this.action === 'DISPATCH_EXIT'; }
  blocker(): string {
    if (this.disabledReason) return this.disabledReason;
    if (!this.permissions.hasPermission(this.isTransfer() ? app_permissions.warehousing.transfer.release
      : app_permissions.warehousing.dispatch.release)) return 'You do not have permission to release this warehouse work.';
    if (this.dispatch?.gatedByTransferId && this.dispatch.crossDockGateOpen !== true) return 'Waiting for the inbound transfer receipt.';
    if (this.linked() && !this.shipmentNumber() && !this.deliveryNumber()) return 'Linked shipment or delivery number is missing.';
    if (!this.units().length) return 'Reserve stock before confirming outbound work.';
    return '';
  }

  review(): void {
    const action = this.nextAction();
    if (!action || this.blocker() || !this.documentId() || this.busy) return;
    this.action = action;
    this.reason = '';
    this.error = '';
    this.preview = undefined;
    if (!this.linked()) { this.dialogVisible = true; return; }
    this.busy = true;
    this.service.previewShipmentBulkWork({ shipmentNumber: this.shipmentNumber(), deliveryNumber: this.deliveryNumber(),
      action, documents: [{ documentType: this.isTransfer() ? 'TRANSFER' : 'DISPATCH', documentId: this.documentId()! }] })
      .pipe(finalize(() => this.busy = false)).subscribe({
        next: response => {
          if (!response.success || !response.data) {
            this.error = response.message || 'Unable to preview this work.';
            return;
          }
          this.preview = response.data;
          this.dialogVisible = true;
        },
        error: error => this.error = error?.error?.message || 'Unable to preview this work.'
      });
  }

  confirm(): void {
    if (!this.action || !this.reason.trim() || !this.documentId() || this.busy || this.preview?.blockedCount) return;
    this.busy = true;
    const request: Observable<APIResponseWrapper<WarehousingBulkResult | WarehousingDispatch | WarehousingTransfer>> = this.linked()
      ? this.service.commitShipmentBulkWork({ previewToken: this.preview!.token, reason: this.reason.trim(),
          idempotencyKey: crypto.randomUUID(), correlationId: crypto.randomUUID() })
      : this.manualAction(this.action, this.documentId()!, this.reason.trim());
    request.pipe(finalize(() => this.busy = false)).subscribe({
      next: response => {
        const result = this.linked() ? response.data as WarehousingBulkResult : undefined;
        if (!response.success || (result && result.status !== 'COMPLETED')) {
          this.fail(result?.failure || response.message || 'Work was rejected. Create a fresh review.');
          return;
        }
        this.dialogVisible = false;
        this.success = `${this.actionLabel(this.action)} confirmed. ${this.finalAction() ? 'Stock exit posted.' : 'Stock remains on hand.'}`;
        this.changed.emit();
      },
      error: error => this.fail(error?.error?.message || 'Work changed. Refresh and review again.')
    });
  }

  private fail(message: string): void {
    this.error = message;
    this.dialogVisible = false;
    this.changed.emit();
  }

  private manualAction(action: OutboundAction, id: number, reason: string): Observable<APIResponseWrapper<WarehousingDispatch | WarehousingTransfer>> {
    switch (action) {
      case 'PICK': return this.isTransfer() ? this.service.markTransferPicked(id, reason) : this.service.markDispatchPicked(id, reason);
      case 'STAGE': return this.isTransfer() ? this.service.markTransferStaged(id, reason) : this.service.markDispatchStaged(id, reason);
      case 'LOAD': return this.isTransfer() ? this.service.markTransferLoaded(id, reason) : this.service.markDispatchLoaded(id, reason);
      case 'TRANSFER_DISPATCH': return this.service.confirmTransferDispatch(id, reason);
      case 'DISPATCH_EXIT': return this.service.confirmDispatchExit(id, reason);
    }
  }
}
